import React, { useCallback, useMemo } from 'react'
import classnames from 'classnames'
import { intl } from 'lang/intl'

import { get } from 'lodash-es'
import WebinarInfoSidebarComponent from '../../../../components/WebinarInfoSidebarComponent/WebinarInfoSidebarComponent'
// @ts-expect-error — importing js
import HandoutsFeature from '../../../HandoutsFeature'
import { useWebinarColors } from '../../../../hooks/useWebinarColors'

import DigitalEventCard from '../../../WebinarSidebarFeature/components/WebinarSidebarComponent/DigitalEventCard'
import SessionsDrawer from '../../../WebinarSidebarFeature/components/WebinarSidebarComponent/SessionsDrawer/SessionsDrawer'
import WebinarInfoAudienceSidebarComponent from '../WebinarInfoAudienceSidebarComponent/WebinarInfoAudienceSidebarComponent'
import {
  WebinarPanelComponent,
  WebinarSidebarSecondaryPanelComponent,
} from '../../../WebinarSidebarFeature/components'
import useWebinarsQuery, {
  type LiveStatus,
} from '../../../../hooks/useWebinarsQuery'
// @ts-expect-error — importing js
import { PollsFeature } from '../../..'
import Header from './Header'
import AudienceChatPane from './WebinarAudienceChatPane'
import useFeatures from '../../../../hooks/useFeatures'
import { Chat } from '../../../../../Chat/components/Chat/Chat'
import { ActionCard } from '../../../../../ActionCards/components/ActionCard'
import { isAudienceChatEnabled } from '../../../../../Chat/blocs/chatVisibility'
import { useWebinarFormatQuery } from '../../../../hooks/useWebinarFormatQuery'
import { getLiveId } from '../../../../../global/blocs/roomInfo'

import './WebinarAudienceSidebarComponent.scss'

interface WebinarAudienceSidebarProps {
  liveId: string
  uuid: string
  token: string
  localParticipantName: string
  isQuestionsEnabled: boolean
  liveData: Record<string, any> // TODO: type this
  isSecondaryPanelVisible: boolean
  toggleSecondaryPanel: (panel: null | 'sessions') => void
  onAskQuestion: () => void
  togglePollOverlay: Function // TODO: type this
  isInGearMode: boolean
  canPostChat: boolean
  secondaryPanelContentType: null | 'sessions'
}

const WebinarAudienceSidebarComponent = ({
  token,
  uuid,
  isSecondaryPanelVisible,
  toggleSecondaryPanel,
  isQuestionsEnabled,
  onAskQuestion,
  togglePollOverlay,
  isInGearMode,
  liveData,
  canPostChat,
  secondaryPanelContentType,
}: WebinarAudienceSidebarProps) => {
  const webinarColors = useWebinarColors()
  const accentColor = webinarColors.accent.color
  const tabColor = webinarColors.tabs.color
  const textStyle = { color: webinarColors.tabs.highContrast.color }
  const iconColor = webinarColors.sidebar.contrast.transparent('25%')
  const { data: webinars } = useWebinarsQuery({
    liveSeriesId: liveData.live_series_id,
    liveStatus: 'live' as LiveStatus,
    token,
  })

  const numberOfLiveSessions = webinars?.total

  const { HasNewChatEnabled, HasWebinarActionCardsEnabled } = useFeatures()

  const { data: series } = useWebinarFormatQuery(liveData.live_series_id, token)

  const styles = useMemo(
    () => ({
      inputStyle: {
        color: webinarColors.tabs.highContrast.color,
        backgroundColor:
          webinarColors.sidebar.contrast.color === '#000000'
            ? 'rgba(0, 0, 0, 0.05)'
            : 'rgba(0, 0, 0, 0.15)',
      },
      buttonStyle: { backgroundColor: webinarColors.accent.color },
      secondaryButtonStyle: {
        backgroundColor: webinarColors.sidebar.contrast.transparent('20%'),
        color: webinarColors.accent.color,
      },
      progressBarStyles: {
        strokeColor: webinarColors.accent.color,
        trailColor: webinarColors.sidebar.color,
      },
      radioButtonStyles: {
        passiveColor: webinarColors.tabs.contrast.color,
        activeColor: webinarColors.accent.color,
      },
    }),
    [webinarColors]
  )

  const renderPollUnitWrapper = useCallback(
    (pollProps: any) => {
      return (
        <WebinarPanelComponent
          style={{
            color: webinarColors.tabs.highContrast.color,
            backgroundColor: webinarColors.tabs.color,
          }}
          {...pollProps}
          title={
            pollProps.title && intl.formatMessage({ defaultMessage: 'Poll' })
          }
          icon={{
            ...pollProps.icon,
            color: webinarColors.sidebar.contrast.transparent('25%'),
          }}
        />
      )
    },
    [webinarColors]
  )

  const cssVars = {
    '--webinar-color-accent': webinarColors.accent.color,
    '--webinar-color-sidebar': webinarColors.sidebar.color,
    '--webinar-color-tabs': webinarColors.tabs.color,
    '--webinar-color-sidebar-text': webinarColors.sidebar.highContrast.color,
    '--webinar-color-sidebar-ctacontrast': webinarColors.sidebar.contrast.color,
    '--webinar-color-tabs-text': webinarColors.tabs.highContrast.color,
    '--webinar-color-tabs-ctacontrast': webinarColors.tabs.contrast.color,
    '--webinar-color-accent-text': webinarColors.accent.highContrast.color,
    '--webinar-color-accent-ctacontrast': webinarColors.accent.contrast.color,
    '--webinar-color-sidebarSecondaryText':
      webinarColors.tabs.highContrast.transparent('85%'),
    '--webinar-color-inputBox':
      webinarColors.sidebar.contrast.color === '#000000'
        ? 'rgba(0, 0, 0, 0.05)'
        : 'rgba(0, 0, 0, 0.15)',
  } as React.CSSProperties

  const audienceChatPane = HasNewChatEnabled ? (
    <Chat />
  ) : (
    <AudienceChatPane
      canPostChat={canPostChat}
      token={token}
      uuid={uuid}
      styles={styles}
    />
  )

  const isDigitalEvent = !!liveData.live_series_id

  const audienceChatEnabled = isAudienceChatEnabled(
    get(liveData, 'comment_mode')
  )

  return (
    <div className="webinar-audience-sidebar-component" style={cssVars}>
      {isDigitalEvent && (
        <Header
          liveSeriesId={liveData.live_series_id}
          toggleSecondaryPanel={toggleSecondaryPanel}
          secondaryPanelContentType={secondaryPanelContentType}
        />
      )}
      <div
        className={classnames('sidebar-main-panel-wrapper', {
          hidden: isSecondaryPanelVisible,
        })}
        style={{
          backgroundColor: webinarColors.sidebar.color,
          color: webinarColors.sidebar.highContrast.color,
        }}
      >
        <WebinarInfoAudienceSidebarComponent
          liveData={liveData}
          isQuestionsEnabled={isQuestionsEnabled}
          onAskQuestion={onAskQuestion}
          styles={styles}
          isMobileDevice={false}
        />
        {series &&
          series.sessionCount > 0 &&
          series.seriesFormat !== 'series-recurring' && (
            <DigitalEventCard
              title={series.name}
              thumbnailUrl={series.thumbnailUrl}
              numberOfSessions={series.sessionCount}
              numberOfLiveSessions={numberOfLiveSessions}
              onClick={() => toggleSecondaryPanel('sessions')}
            />
          )}

        {HasWebinarActionCardsEnabled && <ActionCard />}
        <PollsFeature
          styles={styles}
          iconColor={iconColor}
          accentColor={accentColor}
          objectToken={token}
          renderPollUnitWrapper={renderPollUnitWrapper}
          disableOverlayTrigger={isInGearMode}
          isPublicPollOnly
          canChangePoll={false}
          isVotingAllowed
          namespace="webinar-main-sidebar-polls"
          siblingFeatureNameSpace="webinar-secondary-sidebar-polls"
          togglePollOverlay={togglePollOverlay}
        />
        <HandoutsFeature
          namespace="webinar-sidebar-handouts"
          liveToken={token}
          style={{ ...textStyle, backgroundColor: tabColor }}
          styles={{ progressBarColor: accentColor }}
          iconColor={iconColor}
          mode="view"
        />
        {audienceChatEnabled ? (
          audienceChatPane
        ) : (
          <WebinarInfoSidebarComponent />
        )}
      </div>
      {isSecondaryPanelVisible && secondaryPanelContentType && (
        <div className="secondary-panel-wrapper">
          <WebinarSidebarSecondaryPanelComponent>
            {
              {
                sessions: (
                  <SessionsDrawer
                    seriesId={liveData.live_series_id}
                    seriesToken={liveData.live_series_token}
                    sessionId={`${getLiveId()}`}
                  />
                ),
              }[secondaryPanelContentType]
            }
          </WebinarSidebarSecondaryPanelComponent>
        </div>
      )}
    </div>
  )
}

export default WebinarAudienceSidebarComponent
